<template>
  <div class="mb-4 h-fit space-y-6 lg:col-span-2 lg:mb-0">
    <LazyMarketingCardContactInfo
      :blok="blok"
      :activities-count="activitiesCount"
      :gifts-count="giftsCount"
      :events-count="eventsCount"
      :packages-count="packagesCount"
    />
    <UTabs
      :items="miniNav"
      :ui="{
        list:
          miniNav.length > 1
            ? 'bg-neutral-900/40 backdrop-blur-lg overflow-hidden rounded-2xl ring ring-neutral-800'
            : 'hidden', // Hide list if only one item
        label: 'font-extrabold',
      }"
      class="w-full"
    >
      <template #hours>
        <UCard
          v-if="blok.enable_opening_hours_table"
          :ui="{
            root: 'bg-neutral-900/60 mt-1 backdrop-blur-lg overflow-hidden',
            body: 'p-0 sm:p-0',
            footer: 'p-2 sm:p-4',
          }"
        >
          <template #header>
            <div class="flex items-center justify-between space-x-2">
              <div>
                <h3 class="text-lg font-extrabold text-white">Opening Hours</h3>
                <p
                  v-if="blok.opening_hours_note !== ''"
                  class="text-base font-medium text-neutral-400"
                >
                  {{ blok.opening_hours_note }}
                </p>
              </div>
              <UIcon
                name="i-lucide-clock"
                class="size-6 shrink-0 text-yellow-400"
              />
            </div>
          </template>
          <table class="w-full text-sm">
            <thead>
              <tr
                class="divide-x divide-neutral-800 border-b border-neutral-800 text-yellow-400"
              >
                <th class="p-2 text-center">Day</th>
                <th class="p-2 text-center">Time</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, indexR) in blok.opening_hours_table"
                :key="indexR"
                class="divide-x divide-neutral-800 border-b border-neutral-800"
              >
                <td
                  v-for="(cell, indexC) in row.table_cells"
                  :key="indexC"
                  class="p-2 text-center"
                >
                  {{ cell.content }}
                </td>
              </tr>
            </tbody>
          </table>
          <template
            v-if="
              (blok.opening_hours_table_link &&
                blok.opening_hours_table_link.title) ||
              (blok.opening_hours_table_secondary_link &&
                blok.opening_hours_table_secondary_link.title)
            "
            #footer
          >
            <div class="flex flex-col space-y-2">
              <span
                v-if="
                  blok.opening_hours_table_link &&
                  blok.opening_hours_table_link.title
                "
              >
                <UButton
                  v-if="
                    blok.opening_hours_table_link &&
                    blok.opening_hours_table_link.linktype === 'story'
                  "
                  size="xl"
                  variant="subtle"
                  :to="`/${blok.opening_hours_table_link.cached_url}`"
                  :target="blok.opening_hours_table_link.target"
                  class="w-full justify-center text-center"
                >
                  {{ blok.opening_hours_table_link.title }}
                </UButton>
                <UButton
                  v-else
                  size="xl"
                  variant="subtle"
                  :to="blok.opening_hours_table_link.url"
                  :target="blok.opening_hours_table_link.target"
                  class="w-full justify-center text-center"
                >
                  {{ blok.opening_hours_table_link.title }}
                </UButton>
              </span>
              <span
                v-if="
                  blok.opening_hours_table_secondary_link &&
                  blok.opening_hours_table_secondary_link.title
                "
              >
                <UButton
                  v-if="
                    blok.opening_hours_table_secondary_link.linktype === 'story'
                  "
                  size="xl"
                  variant="outline"
                  :to="`/${blok.opening_hours_table_secondary_link.cached_url}`"
                  :target="blok.opening_hours_table_secondary_link.target"
                  class="w-full justify-center text-center"
                >
                  {{ blok.opening_hours_table_secondary_link.title }}
                </UButton>
                <UButton
                  v-else
                  size="xl"
                  variant="outline"
                  :to="blok.opening_hours_table_secondary_link.url"
                  :target="blok.opening_hours_table_secondary_link.target"
                  class="w-full justify-center text-center"
                >
                  {{ blok.opening_hours_table_secondary_link.title }}
                </UButton>
              </span>
            </div>
          </template>
        </UCard>
      </template>
      <template #prices>
        <UCard
          v-if="blok.enable_entry_prices_table"
          :ui="{
            root: 'bg-neutral-900/60 mt-1 backdrop-blur-lg overflow-hidden',
            body: 'p-0 sm:p-0 border-none',
            footer: 'p-2 sm:p-4',
          }"
        >
          <template #header>
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-extrabold text-white">Entry Prices</h3>
                <p class="mt-0.5 text-sm text-neutral-400">
                  CONCESSIONS: Under 18 / Over 65 / Students / Blue Light /
                  Armed Forces
                </p>
              </div>
              <UIcon
                name="i-lucide-pound-sterling"
                class="size-6 shrink-0 text-yellow-400"
              />
            </div>
          </template>
          <table class="w-full text-sm">
            <thead>
              <tr
                class="divide-x divide-neutral-800 border-b border-neutral-800 text-yellow-400"
              >
                <th class="p-2 text-center">Pass</th>
                <th class="p-2 text-center">Adult / Concession</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, indexR) in blok.entry_prices_table"
                :key="indexR"
                class="divide-x divide-neutral-800 border-b border-neutral-800 font-medium"
              >
                <td
                  v-for="(cell, indexC) in row.table_cells"
                  :key="indexC"
                  class="p-2 text-center"
                >
                  {{ cell.content }}
                </td>
              </tr>
            </tbody>
          </table>
          <template
            v-if="
              blok.entry_prices_table_link?.title ||
              blok.entry_prices_table_footer
            "
            #footer
          >
            <div class="space-y-4">
              <UButton
                v-if="blok.entry_prices_table_link?.linktype === 'story'"
                size="xl"
                variant="subtle"
                :to="`/${blok.entry_prices_table_link?.cached_url}`"
                class="w-full justify-center text-center"
              >
                {{ blok.entry_prices_table_link?.title }}
              </UButton>
              <UButton
                v-else-if="blok.entry_prices_table_link"
                size="xl"
                variant="subtle"
                :to="blok.entry_prices_table_link.url"
                class="w-full justify-center text-center"
              >
                {{ blok.entry_prices_table_link.title }}
              </UButton>
            </div>
          </template>
        </UCard>
        <p
          v-if="blok.entry_prices_table_footer"
          class="mt-4 text-center text-sm font-bold whitespace-pre-line text-yellow-400"
        >
          {{ blok.entry_prices_table_footer }}
        </p>
      </template>
      <template #rental>
        <UCard
          v-if="blok.enable_rental_prices_table"
          :ui="{
            root: 'bg-neutral-900/60 mt-1 backdrop-blur-lg overflow-hidden',
            body: 'p-0 sm:p-0 border-none',
          }"
        >
          <template #header>
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-extrabold text-white">
                  Equipment Rental
                </h3>
              </div>
              <UIcon
                name="i-lucide-pound-sterling"
                class="size-6 shrink-0 text-yellow-400"
              />
            </div>
          </template>
          <table class="w-full text-sm">
            <thead>
              <tr
                class="divide-x divide-neutral-800 border-b border-neutral-800 text-yellow-400"
              >
                <th class="p-2 text-center">Equipment</th>
                <th class="p-2 text-center">Price</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, indexR) in blok.rental_prices_table"
                :key="indexR"
                class="divide-x divide-neutral-800 border-b border-neutral-800 font-medium"
              >
                <td
                  v-for="(cell, indexC) in row.table_cells"
                  :key="indexC"
                  class="p-2 text-center"
                >
                  {{ cell.content }}
                </td>
              </tr>
            </tbody>
          </table>
        </UCard>
      </template>
    </UTabs>
    <div
      v-if="blok.faqs && blok.faqs.length !== 0"
      class="mt-6 hidden lg:block"
    >
      <LazyMarketingContentFaqs :blok="blok" :faqs="blok.faqs" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  LocationStoryblok,
  TableRowStoryblok,
} from '~/types/component-types-sb.d.ts';
import type { TabsItem } from '#ui/types';

interface Props {
  blok: LocationStoryblok;
  activitiesCount: number;
  giftsCount: number;
  eventsCount: number;
  packagesCount: number;
}

const props = defineProps<Props>();

const miniNav = computed<TabsItem[]>(() => {
  const items: TabsItem[] = [];

  if (props.blok.enable_opening_hours_table) {
    items.push({
      label: 'Opening Hours',
      slot: 'hours',
    });
  }

  if (props.blok.enable_entry_prices_table) {
    items.push({
      label: 'Entry Prices',
      slot: 'prices',
    });
  }

  if (props.blok.enable_rental_prices_table) {
    items.push({
      label: 'Rental',
      slot: 'rental',
    });
  }

  return items;
});

// Function to extract postcode and remove it from the address
const processAddress = (address: string) => {
  const postcodePattern = /[A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2}/;
  const match = address.match(postcodePattern);
  const postcode = match ? match[0] : 'No postcode found';
  const cleanedAddress = match ? address.replace(postcode, '').trim() : address;
  return { postcode, cleanedAddress };
};

// Extract postcode and street address from blok.address
const { postcode, cleanedAddress } = processAddress(props.blok.address || '');

// Function to extract opens and closes from timeRange using regex
const extractOpeningHours = (timeRange: string) => {
  const timePattern = /(\d{1,2}:\d{2})\s*[–-]\s*(\d{1,2}:\d{2})/; // Regex for time format
  const match = timeRange.match(timePattern);
  if (match) {
    return { opens: match[1], closes: match[2] };
  } else {
    console.error('Invalid time format:', timeRange);
    return { opens: '00:00', closes: '00:00' }; // Fallback values
  }
};

// Safeguard mapping for openingHoursSpecification
const openingHoursSpecification = props.blok.opening_hours_table
  ? props.blok.opening_hours_table.map((row: TableRowStoryblok) => {
      const dayOfWeek = row?.table_cells?.[0]?.content || 'Unknown';
      const timeRange = row?.table_cells?.[1]?.content || '00:00–00:00';

      // Use regex to extract opens and closes
      const { opens, closes } = extractOpeningHours(timeRange);

      return {
        dayOfWeek: dayOfWeek,
        opens: opens || '00:00', // Assign proper default values if missing
        closes: closes || '00:00',
      };
    })
  : [];

// Define local business schema using dynamic location data
useSchemaOrg([
  defineLocalBusiness({
    name: 'Live For Today: ' + props.blok.headline,
    logo: 'https://a.storyblok.com/f/308362/1080x1080/a4d93ae358/live-for-today-logo-white.png',
    address: {
      addressCountry: 'United Kingdom',
      postalCode: postcode,
      streetAddress: cleanedAddress,
    },
    telephone: props.blok.phone_number,
    openingHoursSpecification: openingHoursSpecification,
  }),
]);
</script>
